import { useCallback, useRef, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import {
  idDocumentSchema,
  type IdDocumentFormValues,
} from '~domains/profile/entities/person';

import { ampli } from '~shared/analytics';
import { config } from '~shared/config';
import { FEATURE_FLAGS } from '~shared/growthbook';
import { FileUpload, type FileUploadRef } from '~shared/ui/file-upload/rhf';
import { FooterWithProgressBar } from '~shared/ui/footer-with-progress-bar';
import { Screen } from '~shared/ui/screen';

import { Select } from '@breezeventures/ui-kit/select/rhf';
import { toast } from '@breezeventures/ui-kit/toast';
import { Typography } from '@breezeventures/ui-kit/typography';
import { InputMasked } from '@breezeventures/web-ph-components/input-masked';
import {
  type KycDocType,
  useConfirmOcr,
  useOcrProcessing,
} from '@breezeventures/web-ph-person/model/kyc';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { zodResolver } from '@hookform/resolvers/zod';

import { PersonFormHeader } from '../../../person-form-header';
import {
  documentMetaMap,
  idDocumentTypeNames,
  idDocumentTypeOptions,
  idDocumentUploadLabels,
} from '../lib';

type IdDocumentProps = {
  initialValues?: Partial<IdDocumentFormValues>;
  onBack: () => void;
  onSubmit: (values: IdDocumentFormValues) => void;
};

export const IdDocument = ({
  initialValues,
  onBack,
  onSubmit,
}: IdDocumentProps) => {
  const fileUploadRef = useRef<FileUploadRef>(null);
  const methods = useForm<IdDocumentFormValues>({
    resolver: zodResolver(idDocumentSchema),
    mode: 'all',
    defaultValues: initialValues ?? {
      type: undefined,
      idNumber: '',
      document: undefined,
    },
  });

  const selectedType = methods.watch('type');

  const [isIdNumberVisible, setIsIdNumberVisible] = useState(false);

  const isMySssOn = useFeatureIsOn(FEATURE_FLAGS.MY_SSS);
  const typeOptions = isMySssOn
    ? idDocumentTypeOptions
    : idDocumentTypeOptions.filter((option) => option.value !== 'MY_SSS');

  const { start: startOcrProcessing } = useOcrProcessing({
    apiHost: config.apiHost,
    applicationId: undefined,
    contextType: undefined,
  });
  const { mutateAsync: confirmOcr } = useConfirmOcr({
    apiHost: config.apiHost,
  });

  const runOcrProcessing = useCallback(
    (file: File, docType: KycDocType, signal?: AbortSignal) =>
      new Promise<{ transactionId: string; idNumber: string | null }>(
        (resolve, reject) => {
          startOcrProcessing(
            file,
            docType,
            {
              onSuccess: (idNumber, transactionId) =>
                resolve({ transactionId, idNumber }),
              onUploadError: () =>
                reject(new Error('Upload failed. Please try again.')),
              onUnsupportedImageType: () =>
                reject(
                  new Error(
                    'Unsupported image. Please upload a PNG or JPG photo.'
                  )
                ),
              onOcrNoResult: () =>
                reject(
                  new Error(
                    "We couldn't read your ID. Please retake a clear photo and try again."
                  )
                ),
              onCardTypeMismatch: () =>
                reject(
                  new Error(
                    "This photo doesn't match the selected ID type. Please check and try again."
                  )
                ),
            },
            signal
          );
        }
      ),
    [startOcrProcessing]
  );

  const handleUpload = useCallback(
    async (
      request: { document: File },
      options?: { signal?: AbortSignal }
    ): Promise<{ documentId: string; fileName?: string }> => {
      if (!selectedType) {
        throw new Error('Select an ID type before uploading.');
      }

      setIsIdNumberVisible(false);

      const { transactionId, idNumber } = await runOcrProcessing(
        request.document,
        selectedType,
        options?.signal
      );

      if (idNumber) {
        methods.setValue('idNumber', idNumber, { shouldValidate: true });
      }
      setIsIdNumberVisible(true);

      ampli.mpProfileIdDocumentUploaded();
      return {
        documentId: transactionId,
        fileName: request.document.name,
      };
    },
    [runOcrProcessing, selectedType, methods]
  );

  const handleSubmit = async (values: IdDocumentFormValues) => {
    if (fileUploadRef.current?.checkIsUploading()) {
      toast('Still uploading, boss!', { type: 'info' });
      return;
    }

    await confirmOcr({
      applicationId: undefined,
      confirmedResult: { idNumber: values.idNumber },
      transactionId: values.document.documentId,
    });

    onSubmit(values);
  };

  return (
    <form
      onSubmit={methods.handleSubmit(handleSubmit)}
      className="flex h-full flex-col"
    >
      <Screen
        header={<PersonFormHeader onBack={onBack} />}
        footer={
          <FooterWithProgressBar
            currentStepIndex={2}
            totalSteps={4}
            submitButtonText="Next"
            isLoading={methods.formState.isSubmitting}
            onSubmit={methods.handleSubmit(handleSubmit)}
          />
        }
      >
        <FormProvider {...methods}>
          <div className="flex flex-col gap-3 py-6">
            <Typography.Title size="s">Upload your ID</Typography.Title>
            <Typography.Text color="secondary">
              Take a&nbsp;clear&nbsp;photo of your&nbsp;ID. Make&nbsp;sure
              all&nbsp;details are&nbsp;readable and nothing is&nbsp;covered.
            </Typography.Text>
          </div>

          <div className="flex flex-col gap-3">
            <Controller
              name="type"
              control={methods.control}
              render={(fieldProps) => (
                <Select
                  {...fieldProps}
                  field={{
                    ...fieldProps.field,
                    onChange: (value: string | undefined) => {
                      fieldProps.field.onChange(value);
                      methods.resetField('document');
                      methods.resetField('idNumber');
                      setIsIdNumberVisible(false);
                    },
                  }}
                  label="Type of ID"
                  labelPosition="inner"
                  renderValue={(id) => (id ? idDocumentTypeNames[id] : '')}
                >
                  {typeOptions.map(({ value, label }) => (
                    <Select.Option key={value} value={value}>
                      <Typography.Text>{label}</Typography.Text>
                      <Select.OptionIndicator />
                    </Select.Option>
                  ))}
                </Select>
              )}
            />

            {selectedType && (
              <Controller
                shouldUnregister
                name="document"
                control={methods.control}
                render={(fieldProps) => (
                  <FileUpload
                    ref={fileUploadRef}
                    {...fieldProps}
                    title={idDocumentUploadLabels[selectedType]}
                    options={{
                      allowedExtensions: ['png', 'jpg', 'jpeg'],
                      showProgress: false,
                    }}
                    onUpload={handleUpload}
                    onFileRemove={() => {
                      setIsIdNumberVisible(false);
                      methods.resetField('idNumber');
                    }}
                  />
                )}
              />
            )}

            {isIdNumberVisible && (
              <Controller
                name="idNumber"
                control={methods.control}
                render={(fieldProps) => (
                  <InputMasked
                    {...fieldProps}
                    label={documentMetaMap[selectedType].label}
                    labelPosition="inner"
                    maskitoOptions={
                      documentMetaMap[selectedType].maskitoOptions
                    }
                    placeholder={documentMetaMap[selectedType].placeholder}
                  />
                )}
              />
            )}
          </div>
        </FormProvider>
      </Screen>
    </form>
  );
};
